import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, writeBatch, doc } from 'firebase/firestore';
import { useAuth } from '../contexts/authContext';
import { useNavigate } from 'react-router-dom';

const REQUIRED_COLUMNS = ['prayerText', 'missionalScale', 'anyLevel', 'prayerDescriptor'];

const BulkPrayerUpload = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState([]);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [done, setDone] = useState(false);

  if (!currentUser?.isSuperAdmin) {
    return <div className="p-8 text-center text-red-500">Access denied. Super admin only.</div>;
  }

  const handleFileChange = (e) => {
    setError('');
    setRows([]);
    setPreview([]);
    setDone(false);
    setSuccessCount(0);
    setFailCount(0);
    const f = e.target.files[0];
    setFile(f);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) {
        setError('File must have a header and at least one data row.');
        return;
      }
      const header = lines[0].split('\t');
      const missing = REQUIRED_COLUMNS.filter(col => !header.includes(col));
      if (missing.length) {
        setError('Missing columns: ' + missing.join(', '));
        return;
      }
      const colIdx = header.reduce((acc, col, i) => { acc[col] = i; return acc; }, {});
      const parsedRows = lines.slice(1).map(line => {
        const cells = line.split('\t');
        return {
          prayerText: cells[colIdx['prayerText']] || '',
          missionalScale: Number(cells[colIdx['missionalScale']]),
          anyLevel: cells[colIdx['anyLevel']]?.toLowerCase() === 'true',
          prayerDescriptor: cells[colIdx['prayerDescriptor']] || '',
        };
      });
      setRows(parsedRows);
      setPreview(parsedRows.slice(0, 5));
    };
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    setUploading(true);
    setError('');
    setSuccessCount(0);
    setFailCount(0);
    let success = 0, fail = 0;
    try {
      for (let i = 0; i < rows.length; i += 400) { // Firestore batch limit
        const batch = writeBatch(db);
        rows.slice(i, i + 400).forEach(row => {
          if (!row.prayerText || isNaN(row.missionalScale)) {
            fail++;
            return;
          }
          const newDoc = doc(collection(db, 'prayers'));
          batch.set(newDoc, row);
          success++;
        });
        await batch.commit();
      }
      setSuccessCount(success);
      setFailCount(fail);
      setDone(true);
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Bulk Prayer Upload</h1>
      <button className="mb-4 text-blue-600 hover:underline" onClick={() => navigate(-1)}>&larr; Back to Prayer Management</button>
      <div className="mb-4">
        <input type="file" accept=".tsv,.txt" onChange={handleFileChange} />
      </div>
      {error && <div className="text-red-500 mb-4">{error}</div>}
      {preview.length > 0 && (
        <div className="mb-4">
          <div className="font-semibold mb-2">Preview (first 5 rows):</div>
          <table className="min-w-full bg-white rounded shadow text-xs">
            <thead>
              <tr>
                {REQUIRED_COLUMNS.map(col => (
                  <th key={col} className="py-1 px-2 bg-gray-100 text-gray-700">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((row, i) => (
                <tr key={i} className="border-b">
                  {REQUIRED_COLUMNS.map(col => (
                    <td key={col} className="py-1 px-2">{String(row[col])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && !done && (
        <button
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-500 disabled:opacity-60"
          onClick={handleUpload}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : `Upload ${rows.length} Prayers`}
        </button>
      )}
      {done && (
        <div className="mt-4 text-green-700 font-semibold">
          Upload complete! {successCount} prayers added. {failCount > 0 && `${failCount} failed.`}
        </div>
      )}
    </div>
  );
};

export default BulkPrayerUpload; 